import React, { useState, useEffect } from "react";
import { fetchApi } from "../../config";

const POSITIONS = [
  "PHP Developer",
  "React Native Developer",
  "SEO Specialist",
  "Salesforce Developer",
  "Other",
];

const POINTS = [
  "Graduate, with good written & spoken English",
  "Portfolio or GitHub always helps",
  "Referrals welcome",
];

/**
 * @param {string} position — controlled from the Career page so "Apply now"
 *                            on a job card can preselect the role.
 */
export default function ApplyForm({ position = "", onPositionChange, positions: propPositions = null }) {
  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "",
    message: "",
  });
  const [positions, setPositions] = useState(() => propPositions && Array.isArray(propPositions) ? propPositions : POSITIONS);
  const [posLoading, setPosLoading] = useState(false);
  const [posError, setPosError] = useState("");
  const [resume, setResume] = useState(null);
  const [status, setStatus] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // If parent passed positions, skip fetching
    if (propPositions && Array.isArray(propPositions)) return;

    let mounted = true;
    async function loadPositions() {
      setPosLoading(true);
      try {
        const res = await fetchApi("get_vacancies.php");
        if (!mounted) return;
        if (res && Array.isArray(res.Vacancies)) {
          const titles = res.Vacancies.map((v) => v.job_title).filter(Boolean);
          if (titles.length) setPositions(titles);
        }
      } catch (err) {
        setPosError(err.message || "Failed to load positions");
      } finally {
        setPosLoading(false);
      }
    }
    loadPositions();
    return () => {
      mounted = false;
    };
  }, []);

  const validateField = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : "";
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRe = /^[+()\-\s\d]{7,}$/;

    if (name === "name") {
      if (!trimmed) return "Please enter your full name.";
      if (trimmed.length < 2) return "That name looks too short.";
    } else if (name === "email") {
      if (!trimmed) return "Please enter your email address.";
      if (!emailRe.test(trimmed)) return "Please enter a valid email address.";
    } else if (name === "phone") {
      if (!trimmed) return "Please enter a phone number.";
      if (!phoneRe.test(trimmed)) return "Please enter a valid phone number.";
    } else if (name === "position") {
      if (!trimmed) return "Please choose a position.";
    } else if (name === "experience") {
      if (!trimmed) return "Please choose a experience.";
    } else if (name === "resume") {
      if (!resume) return "Please attach your resume.";
    } else if (name === "message") {
      if (!trimmed) return "Please add a short cover note.";
      if (trimmed.length < 10)
        return "A few more details would help (10+ characters).";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateAll = () => {
    const nextErrors = {};
    const fields = [
      ["name", values.name],
      ["email", values.email],
      ["phone", values.phone],
      ["position", position],
      ["experience", values.experience],
      ["resume", resume],
      ["message", values.message],
    ];
    fields.forEach(([name, value]) => {
      const message = validateField(name, value);
      if (message) nextErrors[name] = message;
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) {
      return;
    }
    setStatus("sending");
    try {
      const data = new FormData();
      // API expects these keys: name, email, phone, position, experience, message
      Object.entries({ ...values, position }).forEach(([k, v]) =>
        data.append(k, v),
      );
      // server expects file field name `resumeId`
      if (resume) data.append("resumeId", resume);

      const response = await fetchApi("career_form_submit.php", {
        method: "POST",
        body: data,
      });

      if (response && response.success) {
        setStatus("sent");
        setValues({ name: "", email: "", phone: "", experience: "", message: "" });
        setResume(null);
        if (onPositionChange) onPositionChange("");
        e.target.reset();
        setErrors({});
      } else {
        const msg = (response && (response.message || response.error)) ||
          "Failed to submit application.";
        setStatus("error");
        setErrors((prev) => ({ ...prev, _form: msg }));
      }
    } catch (err) {
      setStatus("error");
      setErrors((prev) => ({ ...prev, _form: err.message || String(err) }));
    }
  };

  return (
    <section className="section container" id="apply">
      <div className="apply-split">
        <div className="apply-intro">
          <div className="eyebrow">Apply</div>
          <h2 className="h-lg">Tell us about you.</h2>
          <p className="lead">
            Fill in the form and attach your resume — we'll get back within 24
            hours.
          </p>
          <ul className="apply-points">
            {POINTS.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>

        <form className="apply-form" noValidate onSubmit={handleSubmit}>
          <div className="form-grid-2">
            <div className="field">
              <label htmlFor="ap-name">Full name</label>
              <input
                className="input"
                id="ap-name"
                name="name"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Jane Doe"
                required
                value={values.name}
              />
              {errors.name && <div className="error-msg">{errors.name}</div>}
            </div>
            <div className="field">
              <label htmlFor="ap-email">Email</label>
              <input
                className="input"
                id="ap-email"
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="jane@email.com"
                required
                type="email"
                value={values.email}
              />
              {errors.email && <div className="error-msg">{errors.email}</div>}
            </div>
            <div className="field">
              <label htmlFor="ap-phone">Phone</label>
              <input
                className="input"
                id="ap-phone"
                name="phone"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="(123) 456 7890"
                required
                type="tel"
                value={values.phone}
              />
              {errors.phone && <div className="error-msg">{errors.phone}</div>}
            </div>
            <div className="field">
              <label htmlFor="ap-position">Position</label>
              <select
                className="input"
                id="ap-position"
                name="position"
                onBlur={handleBlur}
                onChange={(e) => {
                  if (onPositionChange) onPositionChange(e.target.value);
                  if (errors.position) {
                    setErrors((prev) => ({ ...prev, position: "" }));
                  }
                }}
                required
                value={position}
              >
                <option disabled value="">
                  Select a role…
                </option>
                {posLoading && <option disabled>Loading…</option>}
                {positions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              {errors.position && (
                <div className="error-msg">{errors.position}</div>
              )}
            </div>
            <div className="field">
              <label htmlFor="ap-experience">Experience (years)</label>
              <input
                className="input"
                id="ap-experience"
                name="experience"
                onChange={handleChange}
                placeholder="e.g. 3"
                type="text"
                value={values.experience}
              />
              {errors.experience && (
                <div className="error-msg">{errors.experience}</div>
              )}
            </div>
            <div className="field">
              <label htmlFor="ap-resume">Resume</label>
              <input
                accept="image/*,.pdf"
                className="input input-file"
                id="ap-resume"
                name="resume"
                onBlur={handleBlur}
                onChange={(e) => {
                  setResume(e.target.files[0] || null);
                  if (errors.resume)
                    setErrors((prev) => ({ ...prev, resume: "" }));
                }}
                required
                type="file"
              />
              {errors.resume && (
                <div className="error-msg">{errors.resume}</div>
              )}
            </div>

            <div className="field">
              <label htmlFor="ap-message">Cover note</label>
              <textarea
                className="textarea"
                id="ap-message"
                name="message"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="A few lines about why you'd be a great fit..."
                required
                rows="4"
                value={values.message}
              />
              {errors.message && (
                <div className="error-msg">{errors.message}</div>
              )}
            </div>
          </div>
          <button
            className="btn btn-primary btn--block"
            disabled={status === "sending"}
            type="submit"
          >
            {status === "sending" ? "Submitting…" : "Submit application"}
          </button>

          {status === "sent" && (
            <p className="form-note">
              Thanks — your application is in. We'll be in touch within 24
              hours.
            </p>
          )}
          {status === "error" && (
            <p className="form-note">
              Something went wrong. Please email hr@kudosta.com.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
